/**
 * TrustLifecycleStrip
 * -------------------
 * Compact 4-step horizontal version of the Trust Center data-lifecycle
 * narrative. Replaces the generic security card-grid on the home landing
 * page with concrete numbers (Carelog-style: "no complete recording ever
 * exists" but adapted to text-only product).
 *
 * Re-uses i18n keys from `trust.lifecycle.*` already shipped in all 8
 * locales — no new translation work for body copy.
 */

import { Pencil, Cpu, Database, Trash2, ArrowRight, MapPin, Trash, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useScrollFadeIn, fadeCls, fadeStyle } from "@/hooks/useScrollFadeIn";
import { trackEvent } from "@/lib/analytics";

const STEPS = [
  { key: "input", icon: Pencil },
  { key: "inference", icon: Cpu },
  { key: "storage", icon: Database },
  { key: "deletion", icon: Trash2 },
] as const;

const FACTS = [
  {
    key: "region",
    icon: MapPin,
    labelDefault: "EU-region",
    valueDefault: "AWS Paris (eu-west-3) · AES-256",
  },
  {
    key: "deletion",
    icon: Trash,
    labelDefault: "Sletning",
    valueDefault: "30 dage konto-wide · 1-klik per beslutning",
  },
  {
    key: "training",
    icon: ShieldCheck,
    labelDefault: "Modeltræning",
    valueDefault: "Zero-retention · dine data forlader aldrig EU",
  },
] as const;

export default function TrustLifecycleStrip() {
  const { t } = useTranslation("ui");
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <div ref={ref} className="mx-auto max-w-5xl">
      <div className={`text-center max-w-2xl mx-auto ${fadeCls(isVisible)}`}>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
          {t("home.lifecycle.eyebrow", { defaultValue: "Datalivscyklus" })}
        </p>
        <h2 className="text-2xl font-bold sm:text-3xl">
          {t("home.lifecycle.title", {
            defaultValue: "Sådan rejser dine data gennem Relark — fra input til glemsel",
          })}
        </h2>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          {t("home.lifecycle.lede", {
            defaultValue:
              "Konkrete trin, konkrete tal. Vi viser præcis hvad der sker med din tekst — for at din DPO og IT-chef kan sige ja på 5 minutter, ikke 5 uger.",
          })}
        </p>
      </div>

      {/* Compact 4-step strip — 1 line per step. Full 4-step narrative lives on /trust */}
      <ol className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <li
              key={step.key}
              className={`relative rounded-xl border border-border bg-card p-4 ${fadeCls(isVisible)}`}
              style={fadeStyle(120 + i * 80)}
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <p className="text-sm font-semibold">
                  {t(`home.lifecycle.short.${step.key}.title`, {
                    defaultValue: t(`trust.lifecycle.${step.key}.title`),
                  })}
                </p>
              </div>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                {t(`home.lifecycle.short.${step.key}.body`, {
                  defaultValue: {
                    input: "Tekst, max 2.000 tegn. Ingen lyd, ingen overvågning.",
                    inference: "EU-routet · zero-retention · ingen modeltræning.",
                    storage: "AWS Paris · AES-256 · row-level isoleret.",
                    deletion: "1-klik per beslutning · 30-dages konto-sletning.",
                  }[step.key],
                })}
              </p>
            </li>
          );
        })}
      </ol>

      {/* Concrete facts box — replaces duplicated founder quote */}
      <div
        className={`mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 ${fadeCls(isVisible)}`}
        style={fadeStyle(520)}
      >
        {FACTS.map((f) => {
          const Icon = f.icon;
          return (
            <div
              key={f.key}
              className="rounded-xl border border-primary/20 bg-primary/[0.04] p-4"
            >
              <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                  {t(`home.lifecycle.facts.${f.key}.label`, { defaultValue: f.labelDefault })}
                </p>
              </div>
              <p className="mt-1.5 text-sm font-medium leading-snug text-foreground/90">
                {t(`home.lifecycle.facts.${f.key}.value`, { defaultValue: f.valueDefault })}
              </p>
            </div>
          );
        })}
      </div>

      <div
        className={`mt-8 flex flex-wrap items-center justify-center gap-3 ${fadeCls(isVisible)}`}
        style={fadeStyle(640)}
      >
        <Link
          to="/trust"
          onClick={() => trackEvent("trust_page_view", { source: "home_lifecycle_strip" })}
          className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-card px-5 py-2.5 text-sm font-semibold text-primary hover:bg-primary/10 transition"
        >
          {t("home.lifecycle.cta", { defaultValue: "Se Trust Center" })}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <a
          href="/relark-dpa-template.pdf"
          target="_blank"
          rel="noopener"
          onClick={() => trackEvent("dpa_downloaded", { source: "home_lifecycle_strip" })}
          className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground underline underline-offset-4"
        >
          {t("home.lifecycle.dpaCta", { defaultValue: "Download databehandleraftale" })}
        </a>
      </div>
    </div>
  );
}